import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Activity,
  BarChart3,
  Bell,
  Building2,
  Download,
  History,
  KeyRound,
  LayoutDashboard,
  Loader2,
  LogOut,
  Menu,
  RefreshCw,
  ScanText,
  Search,
  Settings as SettingsIcon,
  ShieldAlert,
  Tv,
  X,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, Me } from "../api";
import { useRole } from "../hooks/useRole";
import { useSwitchAccount } from "../hooks/useSwitchAccount";
import BrandHeader from "./AppBrandHeader";
import ChangePasswordDialog from "./ChangePasswordDialog";
import { StorageRetentionModal } from "./StorageRetentionModal";
import WeaponAlertsDialog from "./WeaponAlertsDialog";
import WeaponAlertsRibbon from "./WeaponAlertsRibbon";
import { WeaponAlertsProvider } from "./WeaponAlertsContext";
import { PipelineControlProvider, usePipelineControl } from "./PipelineControlContext";
import { RecordingDownloadsProvider, useRecordingDownloads } from "./RecordingDownloadsContext";
import { ScopeProvider } from "./ScopeContext";
import { ScopeSelector } from "./ScopeSelector";
import { Select } from "./Select";

// Item 5 + 6: a thin app-wide bar that surfaces (a) an "applying / reloading"
// state whenever a pipeline-affecting action is in flight, and (b) a transient
// notice when the active store changed under this session.
function PipelineControlBanner() {
  const { busy, label, storeChangeNote, clearStoreChangeNote } = usePipelineControl();
  if (busy) {
    return (
      <div className="flex items-center gap-2 border-b border-bf-violet/30 bg-bf-violet/15 px-6 py-2 text-sm text-white">
        <Loader2 size={16} className="animate-spin shrink-0" />
        <span>{label || "Applying changes…"}</span>
      </div>
    );
  }
  if (storeChangeNote) {
    return (
      <div className="flex items-center gap-2 border-b border-emerald-500/30 bg-emerald-500/10 px-6 py-2 text-sm text-emerald-200">
        <RefreshCw size={16} className="shrink-0" />
        <span className="flex-1">{storeChangeNote}</span>
        <button
          type="button"
          onClick={clearStoreChangeNote}
          className="text-emerald-200/70 hover:text-emerald-100"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    );
  }
  return null;
}

type NavItem = {
  to: string;
  label: string;
  icon: typeof Search;
  beta?: boolean;
  // Staff role required to see this item. Unset = everyone.
  minRole?: "viewer" | "admin" | "dev";
};

const navItems: NavItem[] = [
  { to: "/", label: "AI-Powered Search", icon: Search },
  { to: "/monitoring", label: "Live view", icon: Tv },
  { to: "/alerts", label: "Alerts", icon: ShieldAlert },
  { to: "/recordings", label: "Playback", icon: History },
  { to: "/downloads", label: "Saved clips", icon: Download },
  // Operator notices (storage pressure, fleet health) — staff only.
  { to: "/notifications", label: "Notifications", icon: Bell, minRole: "viewer" },
  // Merged technical operations surface (event log + live status + host
  // diagnostics) — dev role only.
  { to: "/operations", label: "Operations", icon: Activity, minRole: "dev" },
  // Live crop + caption feed for pipeline debugging — dev role only.
  { to: "/dev/captions", label: "Live Captions", icon: ScanText, minRole: "dev" },
];

const dashboardItem: NavItem = {
  to: "/dashboard",
  label: "Dashboard",
  icon: LayoutDashboard,
};

const insightsItem: NavItem = {
  to: "/insights",
  label: "Insights",
  icon: BarChart3,
  beta: true,
};

const settingsItem: NavItem = {
  to: "/settings",
  label: "Settings",
  icon: SettingsIcon,
};

const SIDEBAR_EXPANDED = "16rem";
const SIDEBAR_COLLAPSED = "4.5rem";
const SIDEBAR_STORAGE_KEY = "bf-sidebar-collapsed";

function BetaBadge() {
  return (
    <span className="rounded-full bg-bf-violet/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-bf-magenta ring-1 ring-bf-violet/40">
      Beta
    </span>
  );
}

function FooterNavLink({
  item,
  collapsed,
}: {
  item: NavItem;
  collapsed: boolean;
}) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      title={collapsed ? (item.beta ? `${item.label} (Beta)` : item.label) : undefined}
      className={
        "relative flex items-center rounded-lg py-2 text-sm font-medium text-bf-muted transition hover:bg-white/5 hover:text-bf-text " +
        (collapsed ? "justify-center px-2" : "gap-3 px-3")
      }
      activeProps={{
        className:
          "bg-brand-purple/15 text-white ring-1 ring-inset ring-brand-purple/30",
      }}
    >
      <Icon size={18} className="shrink-0" />
      {!collapsed && (
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {item.label}
          {item.beta && <BetaBadge />}
        </span>
      )}
    </Link>
  );
}

const ROLE_LABEL: Record<string, string> = {
  viewer: "Viewer",
  owner: "Owner",
  admin: "Admin",
  dev: "Dev",
};

function roleLine(me: Me): string {
  if (me.viewing_as) return `IntraScene ${me.real_role} · as ${me.customer?.name ?? ""}`;
  if (me.is_staff) return `IntraScene ${me.role}`;
  return `${ROLE_LABEL[me.role] ?? me.role}${me.customer ? ` · ${me.customer.name}` : ""}`;
}

// IntraScene staff can look at the app exactly as one customer sees it. The
// switch is recorded in the audit log on the server.
function ViewAsSwitch() {
  const role = useRole();
  const customers = useQuery({ queryKey: ["customers"], queryFn: api.customers.list });
  const { switchTo, busy } = useSwitchAccount();
  const current = role.viewingAs && role.account ? String(role.account.id) : "";
  return (
    <div className="px-3 pt-3">
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-bf-muted">
        <Building2 size={13} />
        View as customer
      </label>
      <Select
        value={current}
        disabled={busy || customers.isLoading}
        ariaLabel="View as customer"
        onChange={(v) => switchTo(v ? Number(v) : null)}
        options={[
          { value: "", label: "IntraScene (all customers)" },
          ...(customers.data ?? []).map((c) => ({ value: String(c.id), label: c.name })),
        ]}
      />
    </div>
  );
}

function ViewingAsBanner() {
  const role = useRole();
  const { switchTo, busy } = useSwitchAccount();
  return (
    <div className="flex items-center gap-2 border-b border-amber-400/30 bg-amber-400/10 px-6 py-2 text-sm text-amber-100">
      <Building2 size={16} className="shrink-0" />
      <span className="flex-1">
        Viewing as <strong>{role.account?.name}</strong>, with the access of one of its{" "}
        {role.role === "owner" ? "owners" : "viewers"}.
      </span>
      <button
        type="button"
        className="rounded-md px-2 py-1 text-amber-100 ring-1 ring-amber-300/40 hover:bg-amber-300/10 disabled:opacity-50"
        disabled={busy}
        onClick={() => switchTo(null)}
      >
        Back to IntraScene view
      </button>
    </div>
  );
}

function AppShell() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const role = useRole();
  const { jobs } = useRecordingDownloads();
  const preparingDownloads = jobs.filter((j) => j.status === "preparing").length;
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const unreadNotifications = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: api.notifications.unreadCount,
    enabled: role.isStaff,
    refetchInterval: 30_000,
    staleTime: 15_000,
  }).data?.unread ?? 0;
  const [pwOpen, setPwOpen] = useState(false);
  const visibleNav = navItems.filter((it) => !it.minRole || role.atLeast(it.minRole));
  const showDashboard = true;
  const showInsights = true;
  // Customer viewers watch, search and play back; there is nothing for them
  // to set up.
  const showSettings = role.isStaff || role.canManage;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      /* private browsing / storage disabled */
    }
  }, [collapsed]);

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const fillViewport = pathname === "/monitoring" || pathname === "/recordings";

  return (
    <div
      className="bf-app-bg grid h-screen overflow-hidden text-bf-text transition-[grid-template-columns] duration-200 ease-out"
      style={{
        gridTemplateColumns: `${sidebarWidth} 1fr`,
        gridTemplateRows: "auto 1fr",
      }}
    >
      <BrandHeader />

      <aside className="flex min-h-0 flex-col">
        <div className="bf-gradient-border-flat min-h-0 flex-1">
          <div className="bf-gradient-border-inner-flat flex h-full min-h-0 flex-col overflow-hidden bg-bf-surface/95">
            <div
              className={
                "shrink-0 " + (collapsed ? "px-2 pt-3" : "px-3 pt-3")
              }
            >
              <button
                type="button"
                onClick={() => setCollapsed((v) => !v)}
                title="Menu"
                aria-label="Menu"
                className={
                  "flex w-full items-center rounded-lg py-2 text-bf-muted transition hover:bg-white/5 hover:text-bf-text " +
                  (collapsed ? "justify-center px-2" : "px-3")
                }
              >
                <Menu size={18} className="shrink-0" />
              </button>
            </div>
            <hr className="bf-gradient-divider mt-2 shrink-0" />
            <div
              className={
                "shrink-0 " + (collapsed ? "px-2 pt-1.5" : "px-3 pt-1.5")
              }
            >
              <ScopeSelector collapsed={collapsed} />
            </div>
            <hr className="bf-gradient-divider mt-1.5 shrink-0" />
            <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-3 pt-2">
                {visibleNav.map(({ to, label, icon: Icon, beta }) => (
                  <Link
                    key={to}
                    to={to}
                    title={collapsed ? (beta ? `${label} (Beta)` : label) : undefined}
                    className={
                      "relative flex items-center rounded-lg py-2 text-sm font-medium text-bf-muted transition hover:bg-white/5 hover:text-bf-text " +
                      (collapsed ? "justify-center px-2" : "gap-3 px-3")
                    }
                    activeProps={{
                      className:
                        "bg-brand-purple/15 text-white ring-1 ring-inset ring-brand-purple/30",
                    }}
                    activeOptions={{ exact: to === "/" }}
                  >
                    <Icon size={18} className="shrink-0" />
                    {!collapsed && (
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        {label}
                        {beta && <BetaBadge />}
                        {to === "/notifications" && unreadNotifications > 0 && (
                          <span
                            className="ml-auto rounded-full bg-bf-magenta/20 px-1.5 py-0.5 text-[10px] font-semibold text-bf-magenta"
                            aria-label={`${unreadNotifications} unread`}
                          >
                            {unreadNotifications > 99 ? "99+" : unreadNotifications}
                          </span>
                        )}
                        {to === "/downloads" && preparingDownloads > 0 && (
                          <span className="ml-auto rounded-full bg-bf-magenta/20 px-1.5 py-0.5 text-[10px] font-semibold text-bf-magenta">
                            {preparingDownloads}
                          </span>
                        )}
                      </span>
                    )}
                    {collapsed && to === "/notifications" && unreadNotifications > 0 && (
                      <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-bf-magenta" />
                    )}
                    {collapsed && to === "/downloads" && preparingDownloads > 0 && (
                      <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-bf-magenta" />
                    )}
                  </Link>
                ))}
              </nav>

              <div className="mt-auto shrink-0">
                {(showDashboard || showInsights || showSettings) && (
                  <div
                    className={
                      (collapsed ? "px-2 pt-3" : "px-3 pt-3") +
                      " flex flex-col gap-1"
                    }
                  >
                    {showDashboard && (
                      <FooterNavLink item={dashboardItem} collapsed={collapsed} />
                    )}
                    {showInsights && (
                      <FooterNavLink item={insightsItem} collapsed={collapsed} />
                    )}
                    {showSettings && (
                      <FooterNavLink item={settingsItem} collapsed={collapsed} />
                    )}
                  </div>
                )}
                <hr className="bf-gradient-divider mt-3 shrink-0" />
                {role.isRealStaff && !collapsed && <ViewAsSwitch />}
                <div
                  className={collapsed ? "px-2 pb-4 pt-3" : "px-3 pb-4 pt-3"}
                >
                {me.isLoading ? (
                  <div
                    className={
                      "animate-pulse rounded-lg bg-white/5 " +
                      (collapsed ? "mx-auto h-9 w-9" : "h-14")
                    }
                    aria-hidden
                  />
                ) : (
                  me.data &&
                  (collapsed ? (
                    <div className="flex flex-col items-center gap-1">
                      <button
                        type="button"
                        className="flex w-full items-center justify-center rounded-lg p-2 text-bf-muted transition hover:bg-white/5 hover:text-bf-text"
                        title="Change password"
                        aria-label="Change password"
                        onClick={() => setPwOpen(true)}
                      >
                        <KeyRound size={18} />
                      </button>
                      <button
                        type="button"
                        className="flex w-full items-center justify-center rounded-lg p-2 text-bf-muted transition hover:bg-white/5 hover:text-bf-text"
                        title={`Sign out (${me.data.username})`}
                        aria-label="Sign out"
                        onClick={async () => {
                          await api.logout();
                          qc.clear();
                          nav({ to: "/login" });
                        }}
                      >
                        <LogOut size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="bf-gradient-border">
                      <div className="bf-gradient-border-inner flex items-center justify-between p-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{me.data.username}</div>
                          <div className="truncate text-xs text-bf-muted">{roleLine(me.data)}</div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <button
                            type="button"
                            className="text-bf-muted hover:text-bf-text"
                            onClick={() => setPwOpen(true)}
                            title="Change password"
                            aria-label="Change password"
                          >
                            <KeyRound size={18} />
                          </button>
                          <button
                            type="button"
                            className="text-bf-muted hover:text-bf-text"
                            onClick={async () => {
                              await api.logout();
                              qc.clear();
                              nav({ to: "/login" });
                            }}
                            aria-label="Sign out"
                          >
                            <LogOut size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                </div>
              </div>
            </div>
          </div>
        </aside>

        <main className="relative flex min-h-0 min-w-0 flex-col overflow-hidden">
          {role.viewingAs && <ViewingAsBanner />}
          <PipelineControlBanner />
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
            <WeaponAlertsRibbon />
            <div
              className={
                fillViewport
                  ? "flex min-h-0 flex-1 flex-col overflow-auto p-4 md:p-6"
                  : "min-h-0 flex-1 overflow-auto p-6"
              }
            >
              <Outlet />
            </div>
          </div>
        </main>

      <WeaponAlertsDialog />
      <StorageRetentionModal />
      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}

export default function Layout() {
  return (
    <ScopeProvider>
      <PipelineControlProvider>
        <WeaponAlertsProvider>
          <RecordingDownloadsProvider>
            <AppShell />
          </RecordingDownloadsProvider>
        </WeaponAlertsProvider>
      </PipelineControlProvider>
    </ScopeProvider>
  );
}
